import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useWebClientApi } from "src/api";
import {
  fromSalesPerson,
  toSalesPersonUpdateReq,
  type SalesPersonFormData,
} from "src/api/adapters";
import {
  useResponseRedirect,
  useSectionProgress,
  useDealerEmployees,
} from "src/hooks";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import FormLayout from "src/layouts/FormLayout";
import { Col } from "src/components/layout";
import {
  getSalesPersonFormErrors,
  SALES_REP_OPTION_NOT_SURE,
  SALES_REP_OPTION_OTHER,
} from "./SalesPersonPage.validator";
import type { DtoApplication } from "@porchpass/api-client";
import { fieldErrorsAfterSubmit } from "src/forms/fieldErrorsAfterSubmit";
import {
  FormTable,
  FormTableBody,
  Input,
  Select,
  SelectItem,
  SelectSeparator,
  UserAccountIcon,
} from "@porchpass/ui";

export default () => {
  const api = useWebClientApi();
  const sectionProgress = useSectionProgress();
  const {
    formData,
    updateField,
    loading: formDataLoading,
  } = useFormLoad<SalesPersonFormData>(
    {},
    () => api.getApplication(),
    (data) => fromSalesPerson(data as DtoApplication),
  );
  const [submitHandler, submitting, response, hasAttemptedFormSubmission] =
    useDlaFormSubmit((data: SalesPersonFormData) =>
      api.updateApplication(toSalesPersonUpdateReq(data)),
    );
  useResponseRedirect("/../../progress", response);

  // Fetch dealer employees for the dropdown
  const { employees, loading: employeesLoading } = useDealerEmployees();

  // Track selected option for controlled select
  const [selectedOption, setSelectedOption] = useState<string>("");

  // Track manual input values for "Other" option
  const [manualName, setManualName] = useState<string>("");
  const [manualEmail, setManualEmail] = useState<string>("");

  // Track if employees fetch has completed (loading went true → false, or was skipped)
  const employeesLoadingStartedRef = useRef(false);
  const [employeesFetchComplete, setEmployeesFetchComplete] = useState(false);

  // Track when initial mount delay has passed (gives hooks time to start loading)
  const [mountDelayPassed, setMountDelayPassed] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMountDelayPassed(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (employeesLoading) {
      // Fetch started
      employeesLoadingStartedRef.current = true;
    } else if (employeesLoadingStartedRef.current && !employeesFetchComplete) {
      // Loading finished (went true → false)
      setEmployeesFetchComplete(true);
    } else if (
      !employeesLoading &&
      !employeesLoadingStartedRef.current &&
      employees.length > 0 &&
      !employeesFetchComplete
    ) {
      // Edge case: employees arrived without loading state change (e.g., cached/instant)
      setEmployeesFetchComplete(true);
    }
  }, [employeesLoading, employeesFetchComplete, employees.length]);

  // If mount delay has passed but employees haven't started loading,
  // assume employees fetch was skipped (no dealer_id or firebaseUser)
  useEffect(() => {
    if (
      mountDelayPassed &&
      !employeesLoading &&
      !employeesLoadingStartedRef.current &&
      !employeesFetchComplete
    ) {
      // Enough time has passed and employees fetch never started - mark complete
      setEmployeesFetchComplete(true);
    }
  }, [mountDelayPassed, employeesLoading, employeesFetchComplete]);

  // Data is ready when form data has loaded AND employees fetch has completed
  const dataIsReady = !formDataLoading && employeesFetchComplete;

  // Determine if "Other" is selected
  const isOtherSelected = selectedOption === SALES_REP_OPTION_OTHER;

  // Build submission data based on selection
  const submissionData = useMemo(() => {
    // When no employees dropdown is shown, use formData directly (text inputs)
    if (employees.length === 0) {
      return formData;
    }

    // Dropdown is shown - handle selection cases
    if (selectedOption === SALES_REP_OPTION_NOT_SURE) {
      return {
        ...formData,
        sales_rep_name: "Unknown",
        sales_rep_email: "",
      };
    }

    if (selectedOption === SALES_REP_OPTION_OTHER) {
      return {
        ...formData,
        sales_rep_name: manualName,
        sales_rep_email: manualEmail,
      };
    }

    // Real employee selected - find and use their data
    const selectedEmployee = employees.find(
      (emp) => String(emp.id) === selectedOption,
    );
    if (selectedEmployee) {
      const fullName =
        `${selectedEmployee.first_name} ${selectedEmployee.last_name}`.trim();
      return {
        ...formData,
        sales_rep_name: fullName,
        sales_rep_email: selectedEmployee.email,
      };
    }

    // Dropdown shown but no selection made yet
    return {
      ...formData,
      sales_rep_name: "",
      sales_rep_email: "",
    };
  }, [selectedOption, employees, manualName, manualEmail, formData]);

  const validationContext = useMemo(
    () =>
      employees.length > 0
        ? { mode: "dropdown" as const, selectedOption }
        : { mode: "manual" as const },
    [employees.length, selectedOption],
  );

  // Form errors based on submission data
  const formErrors = getSalesPersonFormErrors(
    submissionData,
    validationContext,
  );
  const salesPersonErrors = useMemo(
    () =>
      fieldErrorsAfterSubmit(
        {
          salesRepSelection: formErrors.sales_rep_selection?.message,
          salesRepName: formErrors.sales_rep_name?.message,
          salesRepEmail: formErrors.sales_rep_email?.message,
        },
        hasAttemptedFormSubmission,
      ),
    [
      formErrors.sales_rep_selection,
      formErrors.sales_rep_name,
      formErrors.sales_rep_email,
      hasAttemptedFormSubmission,
    ],
  );

  // Handle selection change
  const handleSelectChange = useCallback((value: string) => {
    setSelectedOption(value);
    // Clear manual inputs when switching away from "Other"
    if (value !== SALES_REP_OPTION_OTHER) {
      setManualName("");
      setManualEmail("");
    }
  }, []);

  // Restore previously saved value when data is ready
  useEffect(() => {
    // Wait until both form data and employees have loaded
    if (!dataIsReady) {
      return;
    }

    // Only restore if we haven't selected anything yet
    if (selectedOption !== "") {
      return;
    }

    // No saved data to restore
    if (!formData.sales_rep_name && !formData.sales_rep_email) {
      return;
    }

    // Check for special "Not sure" / "Unknown" value first
    if (
      formData.sales_rep_name === "Not sure" ||
      formData.sales_rep_name === "Unknown" ||
      formData.sales_rep_email === "null@example.com"
    ) {
      setSelectedOption(SALES_REP_OPTION_NOT_SURE);
      return;
    }

    // Try to match by email address first (more reliable)
    if (formData.sales_rep_email && employees.length > 0) {
      const matchingEmployee = employees.find(
        (emp) => emp.email === formData.sales_rep_email,
      );
      if (matchingEmployee) {
        setSelectedOption(String(matchingEmployee.id));
        return;
      }

      // Saved email does not belong to a dealer employee — restore as Other
      if (formData.sales_rep_name) {
        setSelectedOption(SALES_REP_OPTION_OTHER);
        setManualName(formData.sales_rep_name);
        setManualEmail(formData.sales_rep_email);
        return;
      }
    }

    // Fall back to name matching when no email was saved
    if (formData.sales_rep_name && employees.length > 0) {
      const matchingEmployee = employees.find((emp) => {
        const fullName = `${emp.first_name} ${emp.last_name}`.trim();
        return fullName === formData.sales_rep_name;
      });
      if (matchingEmployee) {
        setSelectedOption(String(matchingEmployee.id));
        return;
      }
    }

    // No match found - use "Other" and populate text fields
    setSelectedOption(SALES_REP_OPTION_OTHER);
    setManualName(formData.sales_rep_name || "");
    setManualEmail(formData.sales_rep_email || "");
  }, [
    dataIsReady,
    selectedOption,
    formData.sales_rep_name,
    formData.sales_rep_email,
    employees,
  ]);

  return (
    <FormLayout
      title="Who helped you with your application?"
      headerTitle="Salesperson Details"
      stepIndex={11}
      footerTitle={sectionProgress["sales_feedback"] ? "Save" : "Next"}
      clickHandler={() => void submitHandler(submissionData, formErrors)}
      isLoading={submitting}
    >
      <Col>
        <FormTable
          title="Sales Representative"
          headerIcon={
            <UserAccountIcon
              variant="bulk-rounded"
              size={16}
              className="text-[#6F41E6]"
            />
          }
        >
          <FormTableBody className="space-y-4">
            {employees.length > 0 ? (
              <>
                <Select
                  label="Sales Representative"
                  placeholder="Select a sales representative"
                  value={selectedOption}
                  onValueChange={handleSelectChange}
                  className="capitalize [&_button]:capitalize"
                  error={
                    hasAttemptedFormSubmission &&
                    !submitting &&
                    !!salesPersonErrors.salesRepSelection
                  }
                  helperText={
                    hasAttemptedFormSubmission && !submitting
                      ? salesPersonErrors.salesRepSelection
                      : undefined
                  }
                  disabled={!dataIsReady}
                >
                  {employees.map((employee) => (
                    <SelectItem
                      className="capitalize"
                      key={String(employee.id)}
                      value={String(employee.id)}
                    >
                      {employee.first_name} {employee.last_name}
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  <SelectItem value={SALES_REP_OPTION_OTHER}>Other</SelectItem>
                  <SelectItem value={SALES_REP_OPTION_NOT_SURE}>
                    I&apos;m not sure
                  </SelectItem>
                </Select>

                {isOtherSelected && (
                  <>
                    <Input
                      label="Sales Representative Name"
                      value={manualName}
                      onChange={(event) => setManualName(event.target.value)}
                      error={!!salesPersonErrors.salesRepName}
                      helperText={salesPersonErrors.salesRepName}
                    />
                    <Input
                      label="Sales Representative Email"
                      value={manualEmail}
                      onChange={(event) => setManualEmail(event.target.value)}
                      error={!!salesPersonErrors.salesRepEmail}
                      helperText={salesPersonErrors.salesRepEmail}
                    />
                  </>
                )}
              </>
            ) : (
              <>
                <Input
                  label="Sales Representative Name"
                  value={formData.sales_rep_name ?? ""}
                  onChange={(event) =>
                    updateField("sales_rep_name")(event.target.value)
                  }
                  error={!!salesPersonErrors.salesRepName}
                  helperText={salesPersonErrors.salesRepName}
                />
                <Input
                  label="Sales Representative Email"
                  value={formData.sales_rep_email ?? ""}
                  onChange={(event) =>
                    updateField("sales_rep_email")(event.target.value)
                  }
                  error={!!salesPersonErrors.salesRepEmail}
                  helperText={salesPersonErrors.salesRepEmail}
                  showOptional
                />
              </>
            )}
          </FormTableBody>
        </FormTable>
      </Col>
    </FormLayout>
  );
};
